import { useCallback, useEffect, useMemo, useState } from "react";
import { HomeStore, rootStore } from "@/src/store";
import {
  useChatRetainModal,
  useIsPc,
  useLoginModalProvider,
  useMemberUser,
  useShellRouter,
} from "@/src/hooks";

import { AlphaPickPricingModalCallable } from "@/src/components/AlphaPick/AlphaPickPricing";
import { I18nUtil } from "@utils/i18n";
import { MemberKeys } from "@/src/screens/Member";
import { ROUTER_PATH } from "@/src/config";
import Translations from "@translate/hooks/useMainTabHeaderLogic";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { openPlanTabUnpaidUnlock } from "@/src/hooks/plan/planTabUnpaidUnlock";
import {
  isStarterMember,
  resolveTopHeaderButton,
} from "@/src/utils/topHeaderUpgradeButton";
import { pubsub } from "utils";
import { useSnapshot } from "valtio";

export const RENEW_THRESHOLD_MINUTES = 48 * 60;

const I18n = I18nUtil.createScoped(Translations);

export function useMainTabHeaderLogic(trackingModuleName: string) {
  const { data: memberInfo } = useMemberUser();
  const router = useShellRouter();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { open: openLoginModal } = useLoginModalProvider();
  const [isPC] = useIsPc();
  const { openRetainModal } = useChatRetainModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const over48hours = !memberInfo?.auto_renewal
    ? Number(memberInfo?.remain_minutes) > RENEW_THRESHOLD_MINUTES
    : false;
  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
    HomeStore.isOpen = true;
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    HomeStore.isOpen = false;
  }, []);

  const onUpgradeClick = useCallback(async () => {
    if (
      trackingModuleName === "edgen_plan_header_upgrade" &&
      isStarterMember(memberInfo)
    ) {
      await openPlanTabUnpaidUnlock(isPC);
      return;
    }

    // Starter → Paywall；付费正常续订 → Pricing Page（默认定位由 selection 推导）
    if (isStarterMember(memberInfo)) {
      await AlphaPickPricingModalCallable({
        isPC,
        trackingModuleName,
        paywallCopyOverrides: {
          title: I18n.t("Someone should watching the market for you."),
          subtitle: I18n.t("Ed already is."),
          features: [
            I18n.t("Tracks your holdings while you're away"),
            I18n.t("Flags risks before they hit"),
            I18n.t("Answers that remember your history"),
          ],
          hideSocialProof: true,
        },
      });
      return;
    }
    router.push(`${ROUTER_PATH.MEMBER}/?tab=${MemberKeys.PRICING}`);
  }, [isPC, memberInfo, router, trackingModuleName]);

  const onRenewClick = useCallback(() => {
    if (over48hours) {
      router.push(`${ROUTER_PATH.MEMBER}/?tab=${MemberKeys.PRICING}`);
      return;
    }
    openRetainModal();
  }, [openRetainModal, over48hours, router]);

  const commonClickHandler = useCallback(
    (onClick?: () => void) => {
      if (!isLoggedIn) {
        openLoginModal?.();
        return;
      }
      onClick?.();
    },
    [isLoggedIn, openLoginModal],
  );

  const labelConfig = useMemo(() => {
    const buttonType = resolveTopHeaderButton(memberInfo);
    if (buttonType === "upgrade") {
      return {
        text: "Upgrade",
        type: "upgrade",
        onClick: onUpgradeClick,
        className: "bg-content_accent4_primary text-content_inverse_primary",
      };
    }
    if (buttonType === "renew") {
      return {
        text: "Renew",
        type: "renew",
        onClick: onRenewClick,
        className:
          " bg-background_accent4_overlay20 text-content_accent4_primary",
      };
    }
    return null;
  }, [memberInfo, onUpgradeClick, onRenewClick]);

  useEffect(() => {
    const unsubscribe = pubsub.on(pubSubKeyMap.OPEN_RENEW_MODAL, onRenewClick);
    return () => {
      unsubscribe();
    };
  }, [isPC, onRenewClick]);

  return {
    labelConfig,
    isMenuOpen,
    openMenu,
    closeMenu,
    commonClickHandler,
  };
}
